/* =============================================================================
   KCC20 price chart engine (framework-agnostic canvas renderer).

   Deliberately free of Angular: the component owns state/inputs, this owns
   pixels. Keeps the render loop out of change detection entirely.

   Data contract mirrors GET /trading/tokens/:tokenId/candles
   (Kcc20Candle): bucketTimeMs + open/high/low/closeUnitPriceSompi +
   volumeSompi + tokenVolume + tradeCount. Unit prices are SOMPI; converted to
   KAS (1 KAS = 1e8 sompi) for display only.
   ========================================================================== */

import type { Kcc20Candle } from '../../services/trading/kcc20-trading-api.service';

export const SOMPI = 1e8;

export type Kcc20ChartMode = 'candles' | 'line' | 'area';

/** A candle in display space (KAS), derived from the sompi DTO. */
export interface Kcc20ChartBar {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
  tokenVolume: number;
  trades: number;
  /** True when synthesised to fill a no-trade bucket (carry-forward close). */
  empty?: boolean;
}

/** Max buckets densify will invent between first and last trade. */
export const KCC20_CHART_DENSIFY_CAP = 1000;

export interface Kcc20ChartViewportState {
  from: number;
  to: number;
  visibleCount: number;
  atRightEdge: boolean;
  total: number;
}

export interface Kcc20ChartHover {
  index: number;
  bar: Kcc20ChartBar;
  x: number;
  y: number;
  decimals: number;
}

export interface Kcc20ChartOptions {
  mode?: Kcc20ChartMode;
  showVolume?: boolean;
  showGrid?: boolean;
  onViewportChange?: (state: Kcc20ChartViewportState) => void;
  onHover?: (hover: Kcc20ChartHover | null) => void;
}

interface Palette {
  accent: string;
  accentStrong: string;
  buy: string;
  sell: string;
  txt1: string;
  txt2: string;
  txt3: string;
  txt4: string;
  line: string;
  lineFaint: string;
  panel: string;
  inset: string;
  contrast: string;
}

function readTheme(host: HTMLElement): Palette {
  const cs = getComputedStyle(host);
  const v = (n: string, f: string) =>
    (cs.getPropertyValue(n) || '').trim() || f;
  return {
    accent: v('--accent', '#6fc7ba'),
    accentStrong: v('--accent-strong', '#15ffb5'),
    buy: v('--buy', '#3fc753'),
    sell: v('--sell', '#e04242'),
    txt1: v('--txt-1', '#f3f6f8'),
    txt2: v('--txt-2', '#9aa4ad'),
    txt3: v('--txt-3', '#6a747d'),
    txt4: v('--txt-4', '#4a535b'),
    line: v('--line', '#1d2227'),
    lineFaint: v('--line-faint', '#14171a'),
    panel: v('--bg-panel', '#111417'),
    inset: v('--bg-inset', '#0b0d0f'),
    contrast: v('--accent-contrast', '#06120f'),
  };
}

export function hexToRgba(hex: string, a: number): string {
  const raw = (hex ?? '').trim();
  // Tokens are hex in practice, but a theme override could hand us rgb()/oklch()
  // — degrade to the colour as authored rather than painting NaN.
  if (!raw.startsWith('#')) {
    const rgb = raw.match(/^rgba?\(([^)]+)\)$/i);
    if (rgb) {
      // Split on commas AND whitespace/slash: `rgb(63 199 83)` (and
      // `rgb(63 199 83 / 50%)`) is what modern tooling serialises, and
      // getPropertyValue hands back custom properties exactly as authored.
      // Splitting on commas alone left g and b undefined, producing
      // "rgba(63, undefined, undefined, .3)" — which canvas silently ignores on
      // fillStyle (bars paint in the previous colour) and throws on
      // addColorStop (area mode).
      const [r, g, b] = rgb[1]
        .split(/[\s,/]+/)
        .filter(Boolean)
        .map((p) => parseFloat(p));
      if ([r, g, b].every((v) => Number.isFinite(v))) {
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      return raw;
    }
    return raw;
  }
  const h = raw.slice(1);
  const n =
    // 4-digit (#rgba) expands like 3-digit; without this it fell to slice(0,6)
    // and parsed "abcd" as a 16-bit int, yielding a wrong colour.
    h.length === 3 || h.length === 4
      ? h
          .slice(0, 3)
          .split('')
          .map((c) => c + c)
          .join('')
      : h.slice(0, 6);
  const int = parseInt(n, 16);
  if (!Number.isFinite(int)) return raw;
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${a})`;
}

/** KCC20 unit prices span ~1e-5 to ~1e3 KAS, so decimals must adapt. */
export function priceDecimals(range: number): number {
  if (range >= 100) return 2;
  if (range >= 1) return 3;
  if (range >= 0.01) return 5;
  if (range >= 0.0001) return 6;
  return 8;
}

export function fmtPrice(v: number, decimals: number): string {
  return v.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function fmtCompact(v: number): string {
  if (!isFinite(v)) return '0';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(2)}K`;
  return v.toFixed(2);
}

function niceStep(rough: number): number {
  const exp = Math.floor(Math.log10(rough));
  const frac = rough / Math.pow(10, exp);
  const nice =
    frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
  return nice * Math.pow(10, exp);
}

/** Maps a raw API candle (sompi strings) into display space (KAS numbers). */
export function toChartBar(c: Kcc20Candle): Kcc20ChartBar {
  return {
    t: Number(c.bucketTimeMs),
    o: Number(c.openUnitPriceSompi) / SOMPI,
    h: Number(c.highUnitPriceSompi) / SOMPI,
    l: Number(c.lowUnitPriceSompi) / SOMPI,
    c: Number(c.closeUnitPriceSompi) / SOMPI,
    v: Number(c.volumeSompi) / SOMPI,
    tokenVolume: Number(c.tokenVolume ?? 0),
    trades: Number(c.tradeCount ?? 0),
  };
}

/**
 * Fill missing interval buckets so a thin market still gets a continuous
 * timeline. Empty slots carry the previous close with zero volume.
 *
 * Densifies from the first trade through the current interval bucket. When
 * that span would exceed {@link KCC20_CHART_DENSIFY_CAP}, densifies only the
 * trailing `cap` buckets ending at "now" (keeps 1m on long history safe).
 * If that recent window contains no trades (stale fixture / dead market),
 * falls back to densifying the trade span alone.
 *
 * Input must already be sorted ascending by `t`. Bucket times are expected
 * to sit on the interval grid (as returned by the candles API).
 *
 * @param nowMs Injected for tests — production callers omit it.
 */
export function densifyBars(
  bars: Kcc20ChartBar[],
  intervalMs: number,
  cap = KCC20_CHART_DENSIFY_CAP,
  nowMs = Date.now(),
): Kcc20ChartBar[] {
  if (bars.length < 1 || !(intervalMs > 0)) return bars;
  const lastTrade = bars[bars.length - 1].t;
  // Align "now" to the same grid as the trade buckets (first.t + k*interval),
  // not the UTC epoch — API bucket origins are not always epoch-aligned.
  let first = bars[0].t;
  let end = Math.max(
    lastTrade,
    first + Math.floor((nowMs - first) / intervalMs) * intervalMs,
  );
  if (end < first) return bars;

  let expected = Math.floor((end - first) / intervalMs) + 1;
  if (expected <= bars.length && end === lastTrade) return bars;

  if (expected > cap) {
    first = end - (cap - 1) * intervalMs;
  }

  const byTime = new Map<number, Kcc20ChartBar>();
  for (const bar of bars) {
    if (bar.t >= first && bar.t <= end) byTime.set(bar.t, bar);
  }

  if (!byTime.size) {
    // Recent window is ahead of all loaded history — densify the trade span.
    first = bars[0].t;
    end = lastTrade;
    expected = Math.floor((end - first) / intervalMs) + 1;
    if (expected <= bars.length) return bars;
    if (expected > cap) first = end - (cap - 1) * intervalMs;
    for (const bar of bars) {
      if (bar.t >= first && bar.t <= end) byTime.set(bar.t, bar);
    }
    if (!byTime.size) return bars;
  }

  let prevClose = bars[0].c;
  for (const bar of bars) {
    if (bar.t > first) break;
    prevClose = bar.c;
  }

  const out: Kcc20ChartBar[] = [];
  for (let t = first; t <= end; t += intervalMs) {
    const hit = byTime.get(t);
    if (hit) {
      out.push(hit);
      prevClose = hit.c;
      continue;
    }
    out.push({
      t,
      o: prevClose,
      h: prevClose,
      l: prevClose,
      c: prevClose,
      v: 0,
      tokenVolume: 0,
      trades: 0,
      empty: true,
    });
  }
  return out;
}

const INTRADAY_FMT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};
const DAY_FMT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
const FULL_FMT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

export class Kcc20PriceChartEngine {
  bars: Kcc20ChartBar[] = [];
  mode: Kcc20ChartMode;
  showGrid: boolean;
  intervalMs = 86_400_000;

  private volumeVisible: boolean;

  /** Volume pane visibility. Setter marks the canvas dirty. */
  get showVolume(): boolean {
    return this.volumeVisible;
  }
  set showVolume(value: boolean) {
    if (this.volumeVisible === value) return;
    this.volumeVisible = value;
    this.markDirty();
  }

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly ro: ResizeObserver;
  private theme: Palette;
  private themeObserver: MutationObserver | null = null;
  private raf = 0;
  private w = 1;
  private h = 1;
  private animPhase = 0;
  /**
   * Repaint gate. The token page already polls the orderbook, so burning a
   * frame every 16ms on an unchanged chart is wasted battery: only draw when
   * something actually changed, or while the live pulse is on screen.
   */
  private dirty = true;

  private view = { from: 0, to: 0 };
  private readonly minBars = 8;
  private readonly maxBars = 1500;
  /** Target max px per slot — few bars right-align instead of stretching. */
  private readonly maxSlotPx = 16;
  private yZoom = 1;
  private yOffset = 0;

  private pointer: { x: number; y: number } | null = null;
  private drag: { x: number; fromAtStart: number; toAtStart: number } | null =
    null;
  private priceDrag: { y: number; zoomAtStart: number } | null = null;

  private readonly pad = { top: 14, right: 68, bottom: 26, left: 8 };
  private readonly volRatio = 0.18;

  private readonly onViewportChange?: (s: Kcc20ChartViewportState) => void;
  private readonly onHover?: (h: Kcc20ChartHover | null) => void;

  private onWheel!: (e: WheelEvent) => void;
  private onDown!: (e: PointerEvent) => void;
  private onMove!: (e: PointerEvent) => void;
  private onUp!: (e: PointerEvent) => void;
  private onLeave!: () => void;
  private onDbl!: () => void;
  private onKeyDown!: (e: KeyboardEvent) => void;

  constructor(
    private readonly host: HTMLElement,
    opts: Kcc20ChartOptions = {},
  ) {
    this.mode = opts.mode ?? 'line';
    this.volumeVisible = opts.showVolume !== false;
    this.showGrid = opts.showGrid !== false;
    this.onViewportChange = opts.onViewportChange;
    this.onHover = opts.onHover;

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'kcc20-chart__canvas';
    host.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;
    this.theme = readTheme(host);

    this.bind();
    this.observeTheme();
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(host);
    this.resize();
    this.tick = this.tick.bind(this);
    this.raf = requestAnimationFrame(this.tick);
  }

  destroy(): void {
    cancelAnimationFrame(this.raf);
    this.ro.disconnect();
    this.themeObserver?.disconnect();
    this.unbind();
    this.canvas.remove();
  }

  /** Request a repaint on the next frame. */
  markDirty(): void {
    this.dirty = true;
  }

  /**
   * ThemeService flips `data-theme` on <html>; recolour automatically so the
   * parent never has to remember to call refreshTheme().
   */
  private observeTheme(): void {
    if (typeof MutationObserver === 'undefined') return;
    this.themeObserver = new MutationObserver(() => this.refreshTheme());
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    });
  }

  /* ---- Data ------------------------------------------------------------ */

  setCandles(
    raw: Kcc20Candle[],
    meta: { intervalMs?: number; keepViewport?: boolean } = {},
  ): void {
    const prevLen = this.bars.length;
    const prevSpan = this.view.to - this.view.from;
    const wasAtRightEdge = prevLen === 0 || this.view.to >= prevLen - 0.5;
    const prevFirstTime = this.bars[0]?.t;

    const sorted = (raw ?? [])
      .map(toChartBar)
      // `t` is filtered as well as `c`: a candle with a missing bucketTimeMs
      // yields NaN, which poisons the sort comparator and makes
      // Intl.DateTimeFormat.format(new Date(NaN)) throw out of the draw loop.
      .filter((b) => isFinite(b.c) && Number.isFinite(b.t))
      .sort((a, b) => a.t - b.t);

    if (meta.intervalMs) this.intervalMs = meta.intervalMs;
    this.bars = densifyBars(sorted, this.intervalMs);
    this.markDirty();

    const n = this.bars.length;
    if (!n) {
      this.view = { from: 0, to: 0 };
      return;
    }

    if (meta.keepViewport && prevLen && prevSpan > 0) {
      const span = Math.min(prevSpan, n);
      if (wasAtRightEdge) {
        this.view = { from: n - span, to: n };
      } else {
        // The viewport is an index range, but paging older history prepends
        // bars, so every existing bar shifts right by the number added. Holding
        // the raw index would slide the user backwards onto the page that just
        // arrived — and because that leaves `from` pinned near 0, the parent's
        // left-edge check fires again on the next frame and pages forever
        // without any further input. Re-anchor on the bar the user was actually
        // looking at instead.
        const shift =
          prevFirstTime === undefined
            ? 0
            : Math.max(
                0,
                this.bars.findIndex((b) => b.t >= prevFirstTime),
              );
        const from = this.view.from + shift;
        this.view = { from, to: from + span };
      }
    } else {
      const span = Math.min(
        this.maxBars,
        Math.max(Math.min(n, 120), this.preferredMinVisible()),
      );
      this.view = { from: n - span, to: n };
    }
    this.clampView();
    this.emitViewport();
  }

  setMode(mode: Kcc20ChartMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.markDirty();
  }

  refreshTheme(): void {
    this.theme = readTheme(this.host);
    this.markDirty();
  }

  /* ---- Viewport -------------------------------------------------------- */

  get visibleCount(): number {
    return Math.max(1, this.view.to - this.view.from);
  }
  get atRightEdge(): boolean {
    return this.view.to >= this.bars.length - 0.5;
  }

  zoom(factor: number, anchorRatio = 0.5): void {
    const n = this.bars.length;
    if (!n) return;
    const span = this.visibleCount;
    const maxSpan = Math.min(
      this.maxBars,
      Math.max(n, this.preferredMinVisible()),
    );
    const next = Math.min(Math.max(span * factor, this.minBars), maxSpan);
    const anchorIdx = this.view.from + span * anchorRatio;
    this.view.from = anchorIdx - next * anchorRatio;
    this.view.to = this.view.from + next;
    this.clampView();
    this.emitViewport();
  }

  /** Shift the viewport by a fraction of the visible span (negative = left). */
  panBy(fraction: number): void {
    if (!this.bars.length) return;
    const shift = this.visibleCount * fraction;
    this.view.from += shift;
    this.view.to += shift;
    this.clampView();
    this.emitViewport();
  }

  resetView(): void {
    this.yZoom = 1;
    this.yOffset = 0;
    const n = this.bars.length;
    if (!n) return;
    const span = Math.min(
      this.maxBars,
      Math.max(Math.min(n, 120), this.preferredMinVisible()),
    );
    this.view = { from: n - span, to: n };
    this.clampView();
    this.emitViewport();
  }

  fitAll(): void {
    this.yZoom = 1;
    this.yOffset = 0;
    const n = this.bars.length;
    if (!n) return;
    // Still pad when history is thin so "fit" doesn't stretch 8 days full-width.
    const span = Math.max(n, this.preferredMinVisible());
    this.view = { from: n - span, to: n };
    this.clampView();
    this.emitViewport();
  }

  /** How many slots fit at a readable candle width for the current plot. */
  private preferredMinVisible(): number {
    const w = this.plot.w;
    if (!(w > 0)) return this.minBars;
    return Math.max(this.minBars, Math.ceil(w / this.maxSlotPx));
  }

  private clampView(): void {
    const n = this.bars.length;
    if (!n) {
      this.view = { from: 0, to: 0 };
      return;
    }
    const minVis = this.preferredMinVisible();
    let span = this.view.to - this.view.from;
    span = Math.max(span, this.minBars);
    // Thin history: keep preferred slot width by padding empty space on the left.
    if (n < minVis) span = Math.max(span, minVis);
    else span = Math.max(span, Math.min(minVis, n));
    span = Math.min(span, this.maxBars);

    const maxTo = n + Math.min(span * 0.08, 8);
    const minFrom = Math.min(0, n - span);
    let to = this.view.to;
    let from = to - span;
    if (to > maxTo) {
      to = maxTo;
      from = to - span;
    }
    if (from < minFrom) {
      from = minFrom;
      to = from + span;
    }
    this.view = { from, to };
  }

  private emitViewport(): void {
    this.markDirty();
    this.onViewportChange?.({
      from: this.view.from,
      to: this.view.to,
      visibleCount: this.visibleCount,
      atRightEdge: this.atRightEdge,
      total: this.bars.length,
    });
  }

  /* ---- Geometry -------------------------------------------------------- */

  private resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const r = this.host.getBoundingClientRect();
    this.w = Math.max(1, r.width);
    this.h = Math.max(1, r.height);
    this.canvas.width = Math.round(this.w * dpr);
    this.canvas.height = Math.round(this.h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.markDirty();
  }

  private get plot() {
    const volH = this.showVolume
      ? (this.h - this.pad.top - this.pad.bottom) * this.volRatio
      : 0;
    const x0 = this.pad.left;
    const x1 = this.w - this.pad.right;
    const y0 = this.pad.top;
    const y1 = this.h - this.pad.bottom - volH;
    return {
      x0,
      x1,
      y0,
      y1,
      w: x1 - x0,
      h: y1 - y0,
      volTop: y1 + 6,
      volH: Math.max(0, volH - 6),
    };
  }

  private slice() {
    const n = this.bars.length;
    return {
      a: Math.max(0, Math.floor(this.view.from)),
      b: Math.min(n, Math.ceil(this.view.to)),
    };
  }

  private priceRange(): { min: number; max: number } {
    const { a, b } = this.slice();
    if (b <= a) return { min: 0, max: 1 };
    let min = Infinity,
      max = -Infinity;
    let saw = false;
    for (let i = a; i < b; i++) {
      const c = this.bars[i];
      // Empty densified slots carry a flat close — ignore them for Y scale so
      // a long no-trade plateau doesn't dominate the range alone.
      if (c.empty) continue;
      saw = true;
      if (this.mode === 'candles') {
        if (c.l < min) min = c.l;
        if (c.h > max) max = c.h;
      } else {
        if (c.c < min) min = c.c;
        if (c.c > max) max = c.c;
      }
    }
    if (!saw) {
      for (let i = a; i < b; i++) {
        const c = this.bars[i];
        if (c.c < min) min = c.c;
        if (c.c > max) max = c.c;
      }
    }
    if (!isFinite(min) || !isFinite(max)) return { min: 0, max: 1 };
    if (min === max) {
      const p = Math.abs(min) * 0.05 || 1;
      min -= p;
      max += p;
    }
    const padding = (max - min) * 0.12;
    min -= padding;
    max += padding;
    const mid = (min + max) / 2 + (max - min) * this.yOffset;
    const half = (max - min) / 2 / this.yZoom;
    return { min: mid - half, max: mid + half };
  }

  private xOf(index: number): number {
    const p = this.plot;
    return p.x0 + ((index + 0.5 - this.view.from) / this.visibleCount) * p.w;
  }

  private indexAtX(x: number): number {
    const p = this.plot;
    return this.view.from + ((x - p.x0) / p.w) * this.visibleCount - 0.5;
  }

  private yOf(price: number, r: { min: number; max: number }): number {
    const p = this.plot;
    return p.y1 - ((price - r.min) / (r.max - r.min)) * p.h;
  }

  private get barWidth(): number {
    // Cap width so thin markets don't stretch into fat slabs; ~0.7 slot fill
    // packs like a TradingView ribbon once densified.
    return Math.min(12, Math.max(1, (this.plot.w / this.visibleCount) * 0.7));
  }

  /* ---- Render ---------------------------------------------------------- */

  private tick(): void {
    // Only the live-pulse marker animates on its own; everything else is
    // event-driven, so an idle chart costs nothing.
    try {
      const animating = this.needsAnimation();
      if (animating) this.animPhase += 0.016;
      if (this.dirty || animating) {
        this.dirty = false;
        this.draw();
      }
    } finally {
      // Reschedule even if draw() threw: without this a single bad frame
      // (an unpaintable colour, a malformed bar) would leave the canvas frozen
      // on its last painted frame forever, since markDirty() only sets a flag.
      // The dirty gate means a repeated failure re-throws on state changes
      // rather than once per frame.
      this.raf = requestAnimationFrame(this.tick);
    }
  }

  /** True while the newest bar's pulse is inside the plot area. */
  private needsAnimation(): boolean {
    if (!this.bars.length) return false;
    const lastIndex = this.bars.length - 1;
    if (lastIndex < this.view.from - 1 || lastIndex > this.view.to + 1)
      return false;
    const x = this.xOf(lastIndex);
    const p = this.plot;
    return x >= p.x0 && x <= p.x1;
  }

  private draw(): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.w, this.h);
    if (!this.bars.length) return;
    const range = this.priceRange();
    const decimals = priceDecimals(range.max - range.min);
    this.drawGrid(range, decimals);
    if (this.showVolume) this.drawVolume();
    if (this.mode === 'candles') this.drawCandles(range);
    else this.drawLine(range, this.mode === 'area');
    this.drawLastPrice(range, decimals);
    this.drawTimeAxis();
    this.drawCrosshair(range, decimals);
  }

  private drawGrid(
    range: { min: number; max: number },
    decimals: number,
  ): void {
    const ctx = this.ctx,
      t = this.theme,
      p = this.plot;
    if (!this.showGrid) return;
    const target = Math.max(2, Math.floor(p.h / 52));
    const step = niceStep((range.max - range.min) / target);
    const start = Math.ceil(range.min / step) * step;
    ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
    ctx.textBaseline = 'middle';
    for (let price = start; price <= range.max; price += step) {
      const y = this.yOf(price, range);
      if (y < p.y0 - 1 || y > p.y1 + 1) continue;
      ctx.strokeStyle = t.lineFaint;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(p.x0, Math.round(y) + 0.5);
      ctx.lineTo(p.x1, Math.round(y) + 0.5);
      ctx.stroke();
      ctx.fillStyle = t.txt3;
      ctx.textAlign = 'left';
      ctx.fillText(fmtPrice(price, decimals), p.x1 + 8, y);
    }
    ctx.strokeStyle = t.line;
    ctx.beginPath();
    ctx.moveTo(p.x1 + 0.5, p.y0);
    ctx.lineTo(p.x1 + 0.5, this.h - this.pad.bottom);
    ctx.stroke();
  }

  private drawVolume(): void {
    const ctx = this.ctx,
      t = this.theme,
      p = this.plot;
    const { a, b } = this.slice();
    let maxV = 0;
    for (let i = a; i < b; i++) maxV = Math.max(maxV, this.bars[i].v);
    // Written as `!(maxV > 0)` rather than `maxV <= 0`: one bar with a
    // malformed volumeSompi makes Math.max return NaN, and `NaN <= 0` is false,
    // so the loose form lets NaN through and every bar paints at NaN height —
    // a silently empty volume pane with no error.
    if (!(maxV > 0)) return;
    const bw = this.barWidth;
    for (let i = a; i < b; i++) {
      const c = this.bars[i];
      const hgt = (c.v / maxV) * p.volH;
      ctx.fillStyle = hexToRgba(c.c >= c.o ? t.buy : t.sell, 0.32);
      ctx.fillRect(this.xOf(i) - bw / 2, p.volTop + (p.volH - hgt), bw, hgt);
    }
  }

  private drawCandles(range: { min: number; max: number }): void {
    const ctx = this.ctx,
      t = this.theme;
    const { a, b } = this.slice();
    const bw = this.barWidth;
    const thin = bw < 3;
    for (let i = a; i < b; i++) {
      const c = this.bars[i];
      const x = this.xOf(i);
      const xi = Math.round(x) + 0.5;
      const up = c.c >= c.o;
      const col = up ? t.buy : t.sell;

      // Empty densified buckets reserve X space only — don't paint flat ticks
      // (those looked like stair-step dashed plateaus on thin markets).
      if (c.empty) continue;

      ctx.strokeStyle = col;
      ctx.lineWidth = Math.min(1.4, Math.max(1, bw * 0.14));
      ctx.beginPath();
      ctx.moveTo(xi, this.yOf(c.h, range));
      ctx.lineTo(xi, this.yOf(c.l, range));
      ctx.stroke();
      if (thin) continue;

      const yO = this.yOf(c.o, range);
      const yC = this.yOf(c.c, range);
      const flat = Math.abs(yC - yO) < 0.5;
      ctx.fillStyle = col;
      if (flat) {
        // Classic doji: min 2px body + horizontal crossbar.
        ctx.fillRect(x - bw / 2, yC - 1, bw, 2);
        ctx.strokeStyle = col;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x - bw / 2, yC);
        ctx.lineTo(x + bw / 2, yC);
        ctx.stroke();
      } else {
        const top = Math.min(yO, yC);
        ctx.fillRect(x - bw / 2, top, bw, Math.abs(yC - yO));
      }
    }
  }

  private drawLine(range: { min: number; max: number }, area: boolean): void {
    const ctx = this.ctx,
      t = this.theme,
      p = this.plot;
    const { a, b } = this.slice();
    if (b - a < 1) return;
    const pts: Array<[number, number]> = [];
    for (let i = a; i < b; i++) {
      if (this.bars[i].empty) continue;
      pts.push([this.xOf(i), this.yOf(this.bars[i].c, range)]);
    }
    if (pts.length < 1) return;
    if (area) {
      const grad = ctx.createLinearGradient(0, p.y0, 0, p.y1);
      grad.addColorStop(0, hexToRgba(t.accent, 0.28));
      grad.addColorStop(1, hexToRgba(t.accent, 0));
      ctx.beginPath();
      ctx.moveTo(pts[0][0], p.y1);
      pts.forEach(([x, y]) => ctx.lineTo(x, y));
      ctx.lineTo(pts[pts.length - 1][0], p.y1);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
    }
    ctx.save();
    ctx.shadowColor = hexToRgba(t.accentStrong, 0.55);
    ctx.shadowBlur = 12;
    ctx.strokeStyle = t.accent;
    ctx.lineWidth = 1.8;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.stroke();
    ctx.restore();
  }

  private drawLastPrice(
    range: { min: number; max: number },
    decimals: number,
  ): void {
    const ctx = this.ctx,
      t = this.theme,
      p = this.plot;
    const last = this.bars[this.bars.length - 1];
    if (!last) return;
    const y = this.yOf(last.c, range);
    if (y < p.y0 || y > p.y1) return;
    const col =
      this.mode === 'candles' ? (last.c >= last.o ? t.buy : t.sell) : t.accent;

    ctx.save();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = hexToRgba(col, 0.7);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(p.x0, Math.round(y) + 0.5);
    ctx.lineTo(p.x1, Math.round(y) + 0.5);
    ctx.stroke();
    ctx.restore();

    const lx = this.xOf(this.bars.length - 1);
    if (lx >= p.x0 && lx <= p.x1) {
      const pulse = (Math.sin(this.animPhase * 2) + 1) / 2;
      ctx.fillStyle = hexToRgba(col, 0.18 + pulse * 0.22);
      ctx.beginPath();
      ctx.arc(lx, y, 5 + pulse * 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.arc(lx, y, 2.6, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.font = '600 10px "JetBrains Mono", ui-monospace, monospace';
    const tagW = this.pad.right - 4;
    ctx.fillStyle = col;
    this.roundRect(p.x1 + 3, y - 8, tagW - 6, 16, 3);
    ctx.fill();
    ctx.fillStyle = t.contrast;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(fmtPrice(last.c, decimals), p.x1 + 3 + (tagW - 6) / 2, y);
  }

  private drawTimeAxis(): void {
    const ctx = this.ctx,
      t = this.theme,
      p = this.plot;
    const { a, b } = this.slice();
    if (b - a < 1) return;
    const fmt = new Intl.DateTimeFormat(
      'en-US',
      this.intervalMs < 86_400_000 ? INTRADAY_FMT : DAY_FMT,
    );
    const stride = Math.max(1, Math.ceil(76 / (p.w / this.visibleCount)));
    ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
    ctx.fillStyle = t.txt3;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (let i = Math.ceil(a / stride) * stride; i < b; i += stride) {
      const x = this.xOf(i);
      if (x < p.x0 + 12 || x > p.x1 - 12) continue;
      ctx.fillText(
        fmt.format(new Date(this.bars[i].t)),
        x,
        this.h - this.pad.bottom + 8,
      );
      ctx.strokeStyle = t.lineFaint;
      ctx.beginPath();
      ctx.moveTo(Math.round(x) + 0.5, p.y0);
      ctx.lineTo(Math.round(x) + 0.5, this.h - this.pad.bottom);
      ctx.stroke();
    }
    ctx.strokeStyle = t.line;
    ctx.beginPath();
    ctx.moveTo(p.x0, this.h - this.pad.bottom + 0.5);
    ctx.lineTo(this.w, this.h - this.pad.bottom + 0.5);
    ctx.stroke();
  }

  private drawCrosshair(
    range: { min: number; max: number },
    decimals: number,
  ): void {
    if (!this.pointer || this.drag) return;
    const ctx = this.ctx,
      t = this.theme,
      p = this.plot;
    const { x, y } = this.pointer;
    if (x < p.x0 || x > p.x1 || y < p.y0 || y > this.h - this.pad.bottom)
      return;
    const idx = Math.round(this.indexAtX(x));
    const bar = this.bars[idx];
    if (!bar) return;
    const cx = this.xOf(idx);

    ctx.save();
    ctx.setLineDash([3, 4]);
    ctx.strokeStyle = hexToRgba(t.txt2, 0.5);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(Math.round(cx) + 0.5, p.y0);
    ctx.lineTo(Math.round(cx) + 0.5, this.h - this.pad.bottom);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(p.x0, Math.round(y) + 0.5);
    ctx.lineTo(p.x1, Math.round(y) + 0.5);
    ctx.stroke();
    ctx.restore();

    const price = range.min + ((p.y1 - y) / p.h) * (range.max - range.min);
    ctx.font = '600 10px "JetBrains Mono", ui-monospace, monospace';
    ctx.fillStyle = t.txt1;
    this.roundRect(p.x1 + 3, y - 8, this.pad.right - 10, 16, 3);
    ctx.fill();
    ctx.fillStyle = t.panel;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      fmtPrice(price, decimals),
      p.x1 + 3 + (this.pad.right - 10) / 2,
      y,
    );

    const label = new Intl.DateTimeFormat('en-US', FULL_FMT).format(
      new Date(bar.t),
    );
    ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
    const tw = ctx.measureText(label).width + 14;
    const tx = Math.min(Math.max(cx - tw / 2, p.x0), p.x1 - tw);
    ctx.fillStyle = t.txt1;
    this.roundRect(tx, this.h - this.pad.bottom + 4, tw, 16, 3);
    ctx.fill();
    ctx.fillStyle = t.panel;
    ctx.fillText(label, tx + tw / 2, this.h - this.pad.bottom + 12);

    const my = this.yOf(bar.c, range);
    ctx.strokeStyle = t.txt1;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, my, 3.5, 0, Math.PI * 2);
    ctx.stroke();

    this.onHover?.({ index: idx, bar, x: cx, y: my, decimals });
  }

  private roundRect(
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  /* ---- Interaction ----------------------------------------------------- */

  private bind(): void {
    const el = this.canvas;
    this.onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const p = this.plot;
      const x = e.clientX - r.left;
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      // A trackpad two-finger swipe reports non-zero deltaX AND deltaY on
      // every tick, even when the user's intent is purely horizontal. Reading
      // deltaY unconditionally made that swipe's incidental vertical jitter
      // fire a fixed ~14% zoom on every tick while the chart also seemed to
      // pan — the two fought each other. Pinch-to-zoom reports as wheel with
      // ctrlKey set regardless of axis, so it always means zoom.
      if (!e.ctrlKey && absX > absY) {
        this.panBy(e.deltaX / p.w);
        return;
      }
      // Trackpads emit many small ticks per gesture where a mouse sends a
      // handful of large ones; a fixed step size made trackpad zoom feel
      // frantic. Scale the step by how big this tick actually was instead.
      const magnitude = Math.min(absY, 100) / 100;
      const step = 1 + magnitude * 0.14;
      if (x > p.x1) {
        this.yZoom = Math.min(
          6,
          Math.max(0.4, this.yZoom * (e.deltaY > 0 ? 1 / step : step)),
        );
        this.markDirty();
        return;
      }
      this.zoom(
        e.deltaY > 0 ? step : 1 / step,
        Math.min(Math.max((x - p.x0) / p.w, 0), 1),
      );
    };
    this.onDown = (e) => {
      el.setPointerCapture(e.pointerId);
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left;
      if (x > this.plot.x1)
        this.priceDrag = { y: e.clientY - r.top, zoomAtStart: this.yZoom };
      else
        this.drag = { x, fromAtStart: this.view.from, toAtStart: this.view.to };
      el.style.cursor = 'grabbing';
    };
    this.onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left,
        y = e.clientY - r.top;
      this.pointer = { x, y };
      this.markDirty();
      if (this.priceDrag) {
        this.yZoom = Math.min(
          6,
          Math.max(
            0.4,
            this.priceDrag.zoomAtStart * (1 - (y - this.priceDrag.y) / 260),
          ),
        );
        return;
      }
      if (this.drag) {
        const shift = (this.drag.x - x) * (this.visibleCount / this.plot.w);
        this.view.from = this.drag.fromAtStart + shift;
        this.view.to = this.drag.toAtStart + shift;
        this.clampView();
        this.emitViewport();
      }
    };
    this.onUp = (e) => {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* pointer already released */
      }
      this.drag = null;
      this.priceDrag = null;
      el.style.cursor = 'crosshair';
    };
    this.onLeave = () => {
      this.pointer = null;
      this.markDirty();
      this.onHover?.(null);
    };
    this.onDbl = () => this.resetView();
    this.onKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.panBy(e.shiftKey ? -0.6 : -0.2);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.panBy(e.shiftKey ? 0.6 : 0.2);
      }
    };

    // Focusable so arrow-key panning works after a click, without disturbing
    // the crosshair's pointer-driven hover/drag handling above.
    el.tabIndex = 0;
    el.addEventListener('wheel', this.onWheel, { passive: false });
    el.addEventListener('pointerdown', this.onDown);
    el.addEventListener('pointermove', this.onMove);
    el.addEventListener('pointerup', this.onUp);
    el.addEventListener('pointercancel', this.onUp);
    el.addEventListener('pointerleave', this.onLeave);
    el.addEventListener('dblclick', this.onDbl);
    el.addEventListener('keydown', this.onKeyDown);
  }

  private unbind(): void {
    const el = this.canvas;
    el.removeEventListener('wheel', this.onWheel);
    el.removeEventListener('pointerdown', this.onDown);
    el.removeEventListener('pointermove', this.onMove);
    el.removeEventListener('pointerup', this.onUp);
    el.removeEventListener('pointercancel', this.onUp);
    el.removeEventListener('pointerleave', this.onLeave);
    el.removeEventListener('dblclick', this.onDbl);
    el.removeEventListener('keydown', this.onKeyDown);
  }
}
